import React, { useContext } from 'react';
import {
  any, bool as boolType, oneOf, shape as shapeType, string as stringType,
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ProductPod } from '@thd-olt-component-react/product-pod-v6';
import { Col, Image, Row } from '@thd-olt-component-react/core-ui';
import { Drawer, DrawerItem, DrawerHeader } from '@thd-olt-component-react/drawer';
import classNames from 'classnames/bind';
import { getSkippers, isAppliance } from '../utils/helpers';
import styles from '../../styles/visually-similar-drawer.scss';
import '../../styles/thd-recs-containers.scss';

const cx = classNames.bind(styles);

const NAV_TITLE = 'Currently Unavailable';

const VisuallySimilarDrawerRecommendations = (props) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const drawerWidth = isMobile ? 100 : 500;
  const drawerPosition = isMobile ? 'top' : 'right';

  const {
    anchorProduct,
    handleDrawerClose,
    isDrawerOpen,
    recs,
    schemaName,
    storeId,
    membershipInformation,
    brandTitleMaxLine,
    customEventObject,
    hideBulkPrice,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    showSubscribeAndSave,
    hideAddToCart,
    zipCode,
    noATCFulfillment,
  } = props;

  const defaultVariables = {
    storeId,
    zipCode,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave })
  };

  const { media = [] } = anchorProduct;

  const { products } = recs || {};
  const noImageURL = 'https://images.thdstatic.com/catalog/productImages/No_Image_145.jpg';
  const imageURL = (media?.images?.find((img) => img.subType === 'PRIMARY') || {}).url.replace('<SIZE>', '300');

  return (
    <>
      {products?.length
        && (
          <Drawer
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            position={drawerPosition}
            maxSize={drawerWidth}
            initialItem="visually-similar-items"
            isMobile={isMobile}
            enableScroll
          >
            <DrawerItem name="visually-similar-items">
              <DrawerHeader>
                <Row className="visually-similar-items__navigation">
                  {imageURL && !isMobile && (
                    <div className={cx('visually-similar-items__anchor-image')}>
                      <Image
                        src={imageURL || noImageURL}
                        alt="visually-similar-anchor-image"
                        height="100"
                        width="100"
                        data-testid="anchor-image"
                      />
                    </div>
                  )}
                  {
                    <Col xs="6">
                      <span className={cx('visually-similar-items__description')}> {NAV_TITLE} </span>
                    </Col>
                  }
                </Row>
              </DrawerHeader>

              <div className={cx('visually-similar-items')}>

                <div className={cx('visually-similar-items__spacing')} />

                <span className={cx('visually-similar-items__header')}> Explore Similar Items </span>
                <div className={cx('visually-similar-items__product-pod')}>
                  <QueryProvider
                    dataSource={schemaName}
                    cacheKey={`${schemaName}-recommendations-pods`}
                    defaultVariables={defaultVariables}
                  >
                    {products.map((dynamicProduct, idx) => {
                      const { product } = dynamicProduct;
                      const { itemId } = product;
                      const { preferredPriceFlag } = product.pricing;

                      return (
                        <ProductPod
                          brandTitleMaxLine={brandTitleMaxLine}
                          customEventObject={customEventObject}
                          hideATC={hideAddToCart}
                          hideBulkPrice={hideBulkPrice}
                          hideFavorites={hideFavorites}
                          hideFulfillment={hideFulfillment}
                          hideLimitPerOrder={hideLimitPerOrder}
                          hidePriceBadge={hidePriceBadge}
                          hideRating={hideRating}
                          hideRebates={hideRebates}
                          hideSavingsText={!preferredPriceFlag}
                          preferredPriceFlag
                          hideShippingInfo={hideShippingInfo}
                          hideShopThisCollection={hideShopThisCollection}
                          itemId={itemId}
                          key={itemId}
                          podType="horizontal-simple"
                          showSubscribeAndSave={showSubscribeAndSave}
                          showQuickView
                          storeId={storeId}
                          noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                        />
                      );
                    }
                    )}
                  </QueryProvider>

                </div>
              </div>

            </DrawerItem>
          </Drawer>
        )}
    </>
  );
};

VisuallySimilarDrawerRecommendations.dataModel = ProductPod.dataModel;

VisuallySimilarDrawerRecommendations.displayName = 'VisuallySimilarDrawerRecommendations';

VisuallySimilarDrawerRecommendations.propTypes = {
  ...ProductPod.propTypes,
  anchorProduct: shapeType({}),
  // eslint-disable-next-line react/forbid-prop-types
  recs: any,
  schemaName: stringType,

  // productPodProps
  // product-pod take only these values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  hideAddToCart: boolType,
  hideBulkPrice: boolType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  showSubscribeAndSave: boolType,
  noATCFulfillment: boolType
};

VisuallySimilarDrawerRecommendations.defaultProps = {
  anchorProduct: null,
  recs: null,
  schemaName: 'visuallysimilar_1_0',

  brandTitleMaxLine: 3,
  customEventObject: null,
  hideAddToCart: false,
  hideBulkPrice: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRating: false,
  hideRebates: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  showSubscribeAndSave: false,
  noATCFulfillment: true
};

export default VisuallySimilarDrawerRecommendations;
